import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Filter row used on the Restock page. Bundles the 3 dropdowns
 * (Category / Subcategory / Brand) + a clear-filters button.
 *
 * Owns its own popover open/close state to keep the parent slim.
 */
export const RestockFilterRow = ({
  filterCategory, setFilterCategory,
  filterSubCategory, setFilterSubCategory,
  filterBrand, setFilterBrand,
  parentCategories, filterSubcategories, brands,
  itemSearch, onClearAll,
}) => {
  const [catOpen, setCatOpen] = useState(false);
  const [subCatOpen, setSubCatOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);

  const anyActive =
    filterCategory !== 'all' ||
    filterSubCategory !== 'all' ||
    filterBrand !== 'all' ||
    !!itemSearch;

  return (
    <div className="flex flex-wrap gap-2">
      <Popover open={catOpen} onOpenChange={setCatOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 justify-between rounded-lg border-gray-300 font-normal text-sm min-w-[140px]" data-testid="restock-category-filter">
            <span className="truncate">{filterCategory !== 'all' ? parentCategories.find(c => c.id === filterCategory)?.category_name || 'Category' : 'Category'}</span>
            <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>No results found</CommandEmpty>
              <CommandGroup>
                <CommandItem onSelect={() => { setFilterCategory('all'); setCatOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                  All Categories
                </CommandItem>
                {parentCategories.map(cat => (
                  <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setFilterCategory(cat.id); setCatOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                    {cat.category_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Popover open={subCatOpen} onOpenChange={setSubCatOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 justify-between rounded-lg border-gray-300 font-normal text-sm min-w-[140px]" disabled={filterCategory === 'all'} data-testid="restock-subcategory-filter">
            <span className="truncate">{filterSubCategory !== 'all' ? filterSubcategories.find(c => c.id === filterSubCategory)?.category_name || 'Subcategory' : 'Subcategory'}</span>
            <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>No results found</CommandEmpty>
              <CommandGroup>
                <CommandItem onSelect={() => { setFilterSubCategory('all'); setSubCatOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterSubCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                  All Subcategories
                </CommandItem>
                {filterSubcategories.map(cat => (
                  <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setFilterSubCategory(cat.id); setSubCatOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterSubCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                    {cat.category_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Popover open={brandOpen} onOpenChange={setBrandOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 justify-between rounded-lg border-gray-300 font-normal text-sm min-w-[140px]" data-testid="restock-brand-filter">
            <span className="truncate">{filterBrand !== 'all' ? brands.find(b => b.id === filterBrand)?.brand_name || 'Brand' : 'Brand'}</span>
            <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>No results found</CommandEmpty>
              <CommandGroup>
                <CommandItem onSelect={() => { setFilterBrand('all'); setBrandOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterBrand === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                  All Brands
                </CommandItem>
                {brands.map(brand => (
                  <CommandItem key={brand.id} value={brand.brand_name} onSelect={() => { setFilterBrand(brand.id); setBrandOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterBrand === brand.id ? 'opacity-100' : 'opacity-0'}`} />
                    {brand.brand_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {anyActive && (
        <Button type="button" variant="ghost" size="sm" className="h-9 text-xs text-[#6B7280] hover:text-red-500" onClick={onClearAll} data-testid="restock-clear-filters-btn">
          Clear filters
        </Button>
      )}
    </div>
  );
};

export default RestockFilterRow;
